// Static namespace import so bun --compile bundles zod into the binary.
// Dynamic import("zod") via a string variable evades bun's static analysis
// and fails at runtime inside /$bunfs/. The unwrap (zNS.z ?? zNS.default
// ?? zNS) handles both bun's native ESM and vitest's CJS-interop resolver,
// which return the zod namespace at different keys.
import * as zNS from "zod";
import type { WorkspacePlugin } from "../plugin.js";
import {
  connectorDescribe,
  connectorExec,
  connectorList,
  connectorLoadSkill,
  connectorRead,
  connectorSearch,
  connectorWrite,
  mountList,
  mountRead,
  mountStatus,
  mountSync,
} from "../tools/connectors.js";
import {
  flowFindFlowForSession,
  flowGetSession,
  flowList,
  flowListSessions,
  flowShow,
} from "../tools/flows.js";
import { sessionGet, sessionList } from "../tools/sessions.js";
import {
  skillCheckRequirements,
  skillGetPreamble,
  skillList,
  skillListDomains,
  skillSearch,
  skillShow,
} from "../tools/skills.js";
import {
  workspaceInfo,
  workspaceReload,
  workspaceTestSecret,
  workspaceValidate,
} from "../tools/validator.js";

const zStatic: any = (zNS as any).z ?? (zNS as any).default ?? zNS;

/**
 * One workspace plugin tool — name, description, a zod raw-shape input schema,
 * and an async handler returning a `ToolResult`.
 *
 * Carries **zero MCP/SDK dependency**: the same definitions feed both the
 * in-process Claude Agent SDK server ({@link buildWorkspaceMcpServer}) and the
 * raw stdio MCP server ({@link buildWorkspaceMcpStdioServer}).
 */
export interface WorkspaceToolDef {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  handler: (args: any) => Promise<unknown>;
}

/**
 * Build the 28 workspace plugin tool definitions.
 *
 * Tool groups:
 * - 4 mount tools (`mount_list`, `mount_sync`, `mount_status`, `mount_read`)
 * - 5 flow tools (`flow_list`, `flow_show`, `flow_list_sessions`, `flow_get_session`, `flow_find_flow_for_session`)
 * - 6 skill tools (`skill_list`, `skill_list_domains`, `skill_show`, `skill_search`, `skill_check_requirements`, `skill_get_preamble`)
 * - 2 session tools (`session_list`, `session_get`)
 * - 7 connector tools (`connector_list`, `connector_read`, `connector_write`, `connector_search`, `connector_exec`, `connector_load_skill`, `connector_describe`)
 * - 4 workspace tools (`workspace_validate`, `workspace_info`, `workspace_test_secret`, `workspace_reload`)
 *
 * @param plugin - A booted `WorkspacePlugin` instance.
 * @returns The 28 tool definitions, free of any MCP/SDK coupling.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export function buildWorkspaceToolDefs(plugin: WorkspacePlugin): WorkspaceToolDef[] {
  const z: any = zStatic;

  const driver = plugin.driver ?? "omp";
  const p = plugin.projectDir;

  return [
    // Mount tools
    {
      name: "mount_list",
      description: "List all mounts declared in skaile.yaml with their mount status.",
      inputSchema: {},
      handler: async () => mountList(plugin.connectorManager),
    },
    {
      name: "mount_sync",
      description: "Sync a mount — pull latest from remote (git, S3, WebDAV).",
      inputSchema: { mount_id: z.string().describe("Mount ID from mount_list") },
      handler: async ({ mount_id }: { mount_id: string }) =>
        mountSync(plugin.connectorManager, mount_id),
    },
    {
      name: "mount_status",
      description: "Get the mount status and last sync time for a mount.",
      inputSchema: { mount_id: z.string() },
      handler: async ({ mount_id }: { mount_id: string }) =>
        mountStatus(plugin.connectorManager, mount_id),
    },
    {
      name: "mount_read",
      description: "Read a file from a mounted mount by path.",
      inputSchema: {
        mount_id: z.string(),
        path: z.string().describe("Relative path within the mount"),
      },
      handler: async ({ mount_id, path }: { mount_id: string; path: string }) =>
        mountRead(plugin.connectorManager, mount_id, path),
    },
    // Flow tools
    {
      name: "flow_list",
      description: "List all available flow definitions from ai-assets.",
      inputSchema: {},
      handler: async () => flowList(p),
    },
    {
      name: "flow_show",
      description: "Show full flow definition (nodes + edges) for a flow ID.",
      inputSchema: { flow_id: z.string().describe("Flow ID from flow_list") },
      handler: async ({ flow_id }: { flow_id: string }) => flowShow(p, flow_id),
    },
    {
      name: "flow_list_sessions",
      description: "List past and active flow run sessions for this workspace.",
      inputSchema: {},
      handler: async () => flowListSessions(p),
    },
    {
      name: "flow_get_session",
      description: "Get driver metadata for a specific flow run session.",
      inputSchema: { run_id: z.string().describe("Run ID from flow_list_sessions") },
      handler: async ({ run_id }: { run_id: string }) => flowGetSession(p, run_id),
    },
    {
      name: "flow_find_flow_for_session",
      description: "Resolve and return the flow definition used in a past session.",
      inputSchema: { run_id: z.string() },
      handler: async ({ run_id }: { run_id: string }) => flowFindFlowForSession(p, run_id),
    },
    // Skill tools
    {
      name: "skill_list",
      description: "List all skills in the ai-assets catalog, optionally filtered by domain.",
      inputSchema: { domain: z.string().optional().describe("Domain name filter") },
      handler: async ({ domain }: { domain?: string }) => skillList(p, domain),
    },
    {
      name: "skill_list_domains",
      description: "List all skill domains (top-level ai-assets directories with skills).",
      inputSchema: {},
      handler: async () => skillListDomains(p),
    },
    {
      name: "skill_show",
      description: "Show the full SKILL.md content for a skill ID.",
      inputSchema: { skill_id: z.string().describe("Skill ID from skill_list") },
      handler: async ({ skill_id }: { skill_id: string }) => skillShow(p, skill_id),
    },
    {
      name: "skill_search",
      description: "Search skills by name, description, or keywords.",
      inputSchema: { query: z.string() },
      handler: async ({ query }: { query: string }) => skillSearch(p, query),
    },
    {
      name: "skill_check_requirements",
      description: "Validate that all skill prerequisites are met in the target directory.",
      inputSchema: {
        skill_id: z.string(),
        target_dir: z
          .string()
          .optional()
          .describe("Directory to check against (default: projectDir)"),
      },
      handler: async ({ skill_id, target_dir }: { skill_id: string; target_dir?: string }) =>
        skillCheckRequirements(p, skill_id, target_dir ?? p),
    },
    {
      name: "skill_get_preamble",
      description: "Get the REQUIRES/INPUT/READS/WRITES DSL preamble for a skill.",
      inputSchema: { skill_id: z.string() },
      handler: async ({ skill_id }: { skill_id: string }) => skillGetPreamble(p, skill_id),
    },
    // Session tools
    {
      name: "session_list",
      description: "List all past agent run sessions for this workspace, sorted newest first.",
      inputSchema: {},
      handler: async () => sessionList(p),
    },
    {
      name: "session_get",
      description: "Get full metadata for a specific session by run ID.",
      inputSchema: { run_id: z.string() },
      handler: async ({ run_id }: { run_id: string }) => sessionGet(p, run_id),
    },
    // Connector tools
    {
      name: "connector_list",
      description:
        "List connectors declared in skaile.yaml as {connected, failed}. `failed` holds connectors that did not connect this session, with the failure reason — their mount paths are dead directories, not synced workspaces.",
      inputSchema: {},
      handler: async () => connectorList(plugin.connectorManager),
    },
    {
      name: "connector_read",
      description: "Read content from a connector by path.",
      inputSchema: {
        connector_id: z.string().describe("Connector ID from connector_list"),
        path: z.string().describe("Path within the connector"),
      },
      handler: async ({ connector_id, path }: { connector_id: string; path: string }) =>
        connectorRead(plugin.connectorManager, connector_id, path),
    },
    {
      name: "connector_write",
      description: "Write content to a connector by path.",
      inputSchema: {
        connector_id: z.string(),
        path: z.string(),
        content: z.string().describe("Content to write"),
      },
      handler: async ({
        connector_id,
        path,
        content,
      }: {
        connector_id: string;
        path: string;
        content: string;
      }) => connectorWrite(plugin.connectorManager, connector_id, path, content),
    },
    {
      name: "connector_search",
      description: "Search within a connector by query string.",
      inputSchema: {
        connector_id: z.string(),
        query: z.string().describe("Search query"),
      },
      handler: async ({ connector_id, query }: { connector_id: string; query: string }) =>
        connectorSearch(plugin.connectorManager, connector_id, query),
    },
    {
      name: "connector_exec",
      description: "Execute a custom operation on a connector.",
      inputSchema: {
        connector_id: z.string(),
        operation: z.string().describe("Operation name from connector_describe"),
        args: z.record(z.string(), z.unknown()).describe("Operation arguments"),
      },
      handler: async ({
        connector_id,
        operation,
        args,
      }: {
        connector_id: string;
        operation: string;
        args: Record<string, unknown>;
      }) => connectorExec(plugin.connectorManager, connector_id, operation, args),
    },
    {
      name: "connector_load_skill",
      description: "Load the rendered skill body for a connector (operations + usage).",
      inputSchema: {
        connector_id: z.string(),
      },
      handler: async ({ connector_id }: { connector_id: string }) =>
        connectorLoadSkill(plugin.connectorManager, connector_id),
    },
    {
      name: "connector_describe",
      description: "Describe a connector's capabilities via its RuntimeSkillDescriptor.",
      inputSchema: {
        connector_id: z.string(),
      },
      handler: async ({ connector_id }: { connector_id: string }) =>
        connectorDescribe(plugin.connectorManager, connector_id),
    },
    // Validator tools
    {
      name: "workspace_validate",
      description: "Run a full health check: connectors, mounts, secrets, driver binary.",
      inputSchema: {},
      handler: async () => workspaceValidate(plugin, driver),
    },
    {
      name: "workspace_info",
      description: "Show workspace configuration: driver, model, provider, resource counts.",
      inputSchema: {},
      handler: async () => workspaceInfo(plugin, driver, plugin.provider, plugin.model),
    },
    {
      name: "workspace_test_secret",
      description: "Test resolution of one secret reference (env:VAR, forge:key).",
      inputSchema: { ref: z.string().describe("Secret ref e.g. env:ANTHROPIC_API_KEY") },
      handler: async ({ ref }: { ref: string }) => workspaceTestSecret(ref),
    },
    {
      name: "workspace_reload",
      description: "Re-read skaile.yaml and re-sync the plugin state.",
      inputSchema: {},
      handler: async () => workspaceReload(plugin),
    },
  ];
}

/**
 * Build the **in-process** workspace MCP server via the Claude Agent SDK's
 * `createSdkMcpServer`. This shape is what the runner embeds into a Claude
 * Agent SDK `query()` call — the runner *is* the Claude Agent SDK host.
 *
 * Returns `null` when `@anthropic-ai/claude-agent-sdk` is not installed
 * (graceful degradation for non-Claude-Code environments).
 *
 * For the standalone `skaile mcp-server` stdio subprocess use
 * {@link buildWorkspaceMcpStdioServer} / {@link runMcpServerStdio} instead —
 * those carry no Claude Agent SDK dependency.
 *
 * @param plugin - A booted `WorkspacePlugin` instance.
 * @returns The SDK MCP server object or `null` if the SDK is unavailable.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export async function buildWorkspaceMcpServer(plugin: WorkspacePlugin): Promise<unknown | null> {
  let sdk: any;
  try {
    sdk = await import("@anthropic-ai/claude-agent-sdk");
  } catch {
    return null;
  }
  return sdk.createSdkMcpServer({
    name: "skaile-workspace",
    tools: buildWorkspaceToolDefs(plugin),
  });
}

/**
 * Build a **raw stdio** workspace MCP server on `@modelcontextprotocol/sdk` —
 * no Claude Agent SDK. This is the correct shape for the `skaile mcp-server`
 * stdio subprocess that MCP-client agents (Claude Code, Codex, …) spawn.
 * Runs on plain Node.
 *
 * The returned server is not yet connected — call `.connect(transport)` (or
 * use {@link runMcpServerStdio}).
 *
 * @param plugin - A booted `WorkspacePlugin` instance.
 * @returns A `McpServer` with all 28 workspace tools registered.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export async function buildWorkspaceMcpStdioServer(plugin: WorkspacePlugin): Promise<any> {
  const { McpServer } = await import("@modelcontextprotocol/sdk/server/mcp.js" as string);
  const server = new McpServer({ name: "skaile-workspace", version: "1.0.0" });
  for (const def of buildWorkspaceToolDefs(plugin)) {
    server.registerTool(
      def.name,
      { description: def.description, inputSchema: def.inputSchema },
      def.handler,
    );
  }
  return server;
}

/**
 * Start the workspace MCP server over stdio transport.
 *
 * Builds the raw `@modelcontextprotocol/sdk` server via
 * {@link buildWorkspaceMcpStdioServer} and connects a `StdioServerTransport`.
 * No `@anthropic-ai/claude-agent-sdk` dependency anywhere on this path.
 *
 * @param plugin - A booted `WorkspacePlugin` instance.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export async function runMcpServerStdio(plugin: WorkspacePlugin): Promise<void> {
  const server = await buildWorkspaceMcpStdioServer(plugin);
  const { StdioServerTransport } = await import(
    "@modelcontextprotocol/sdk/server/stdio.js" as string
  );
  await server.connect(new StdioServerTransport());
}
